import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Menu, MenuItem, IconButton } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import "./myAppointments.css";
import { useSelector } from "react-redux";
import SearchHeader from "../searchResults/searchHeader";
import MessageButton from "./assets/messageBtn.png";
import { useNavigate } from "react-router-dom"; // <-- Import useNavigate
import { getPracticeAreaLabel } from "../../helpers/stringHelper";
import ChatModal from "./chatModal";
import { canJoinMeetingNow, formatMeetingWindowLocal, formatCountdown, formatMeetingDetails } from '../../helpers/stringHelper';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { authenticatedFetch } from "../../network/api";

/**
 * A robust menu component using Material-UI
 */
function CardMenu({ onReschedule, onCancel, onViewDocuments }) {
  const [anchorEl, setAnchorEl] = useState(null);

  // Open menu when clicked
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Close menu
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            onViewDocuments();
            handleClose();
          }}
        >
          View Documents
        </MenuItem>
        <MenuItem
          onClick={() => {
            onReschedule();
            handleClose();
          }}
        >
          Reschedule
        </MenuItem>
        <MenuItem
          onClick={() => {
            onCancel();
            handleClose();
          }}
        >
          Cancel Appointment
        </MenuItem>
      </Menu>
    </div>
  );
}

function MyAppointments() {
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("pending"); // 'pending' | 'upcoming' | 'past'
  const userId = useSelector((state) => state.clientData.clientData?.user_id);
  const userInfo = useSelector((state) => state.clientData.clientData);
  const videoToken = useSelector((state) => state.clientData.videoToken);

  // useNavigate hook to programmatically navigate
  const navigate = useNavigate();

  // Chat modal state
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [chatLawyer, setChatLawyer] = useState(null);

  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [joinCountdown, setJoinCountdown] = useState(0);
  const [joinWindowText, setJoinWindowText] = useState("");

  useEffect(() => {
    let timer;
    if (joinModalOpen && joinCountdown > 0) {
      timer = setInterval(() => {
        setJoinCountdown((prev) => (prev > 1000 ? prev - 1000 : 0));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [joinModalOpen, joinCountdown]);

  // Fetch appointments from the API
  useEffect(() => {
    async function fetchAppointments() {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch both confirmed appointments and pending requests using authenticatedFetch
        const [confirmedResponse, pendingResponse] = await Promise.all([
          authenticatedFetch(`/api/client/fetchConfirmedAppointments?client_id=${userId}`),
          authenticatedFetch(`/api/client/fetchPendingAppointmentRequests?client_id=${userId}`)
        ]);
        
        if (!confirmedResponse.ok || !pendingResponse.ok) {
          throw new Error(`Request failed with status ${confirmedResponse.status} or ${pendingResponse.status}`);
        }
        
        const [confirmedData, pendingData] = await Promise.all([
          confirmedResponse.json(),
          pendingResponse.json()
        ]);
        
        setUpcomingAppointments(confirmedData.upcoming || []);
        setPastAppointments(confirmedData.past || []);
        setPendingAppointments(pendingData || []);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching appointments:", err);
      } finally {
        setLoading(false);
      }
    }
    if (userId) {
      fetchAppointments();
    }
  }, [userId]);

  const handleTabChange = (tabValue) => {
    setActiveTab(tabValue);
  };

  const filteredAppointments = (() => {
    switch (activeTab) {
      case "pending":
        return pendingAppointments;
      case "upcoming":
        return upcomingAppointments;
      case "past":
        return pastAppointments;
      default:
        return pendingAppointments;
    }
  })();

  const handleJoinMeeting = (userInfo, appointment) => {
    const startISO = appointment.requestedTimestamp || appointment.appointment_time;
    const endISO = appointment.end_ts;
    const { allowed, countdown, start, end } = canJoinMeetingNow(startISO, endISO);
    if (!allowed) {
      const meetingDetails = formatMeetingDetails(start, end);
      setJoinWindowText(meetingDetails.fullText);
      setJoinCountdown(countdown > 0 ? countdown : 0);
      setJoinModalOpen(true);
      return;
    }
    const url = new URL("/client-video", window.location.origin);

    console.log("lawyer info ", appointment.lawyer);
    console.log("appointment info ", appointment);

    // Pass additional parameters like token, userId, callId, and name
    url.searchParams.append("token", JSON.stringify(videoToken));
    url.searchParams.append("userId", JSON.stringify(userInfo.user_id));
    url.searchParams.append("callId", JSON.stringify(appointment.id));
    url.searchParams.append("name", JSON.stringify(userInfo.first_name + ' ' + userInfo.last_name));

    // Add review parameters
    url.searchParams.append("lawyerName", encodeURIComponent(appointment.lawyer.first_name + ' ' + appointment.lawyer.last_name || ''));
    url.searchParams.append("appointmentId", appointment.id);
    url.searchParams.append("lawyerId", appointment.lawyer.user_id);

    // Open the new window with the URL containing the necessary parameters
    window.open(url, "_blank");
  };

  const handleCardMenuAction = (action, appointment) => {
    switch (action) {
      case "viewDocs":
        if (appointment.documents && appointment.documents.length) {
          window.open(appointment.documents[0], "_blank");
        } else {
          alert("No documents to view for this appointment.");
        }
        break;
      case "reschedule":
        alert(`Rescheduling appointment ID: ${appointment.id}`);
        break;
      case "cancel":
        alert(`Cancelling appointment ID: ${appointment.id}`);
        break;
      default:
        break;
    }
  };

  // Handler for when the message button is clicked
  const handleMessageButtonClick = (lawyer) => {
    setChatLawyer(lawyer);
    setChatModalOpen(true);
  };

  return (
    <div>
      <SearchHeader searchBar={false} headerColor="search-header-gray" />
      <div className="appointments-page">
        <h1 className="appointments-title">My Appointments</h1>

        <div className="appointments-tabs">
          <button
            className={activeTab === "pending" ? "active" : ""}
            onClick={() => handleTabChange("pending")}
          >
            Pending Requests
          </button>
          <button
            className={activeTab === "upcoming" ? "active" : ""}
            onClick={() => handleTabChange("upcoming")}
          >
            Upcoming Appointments
          </button>
          <button
            className={activeTab === "past" ? "active" : ""}
            onClick={() => handleTabChange("past")}
          >
            Past Appointments
          </button>
        </div>

        {loading && <div>Loading appointments...</div>}
        {error && <div className="error">Error: {error}</div>}

        {!loading && !error && (
          <div className="appointments-list">
            {filteredAppointments.length === 0 ? (
              <p>No appointments found.</p>
            ) : (
              filteredAppointments.map((appt) => {
                const {
                  id,
                  status,
                  requestedTimestamp,
                  documents = [],
                  lawyer = {},
                  appointmentType = {},
                } = appt;
                const lawyerPhoto =
                  lawyer.profile_photo_url || "https://via.placeholder.com/50";
                const lawyerName = lawyer.name || "Lawyer Name";
                // Assumes lawyer has an "id" property. Adjust accordingly if it is named differently.
                const lawyerId = lawyer.user_id;
                const practiceAreas = Array.isArray(lawyer.practice_areas)
                  ? lawyer.practice_areas
                      .map((area) => getPracticeAreaLabel(area))
                      .join(", ")
                  : "No practice areas";
                const serviceType =
                  appointmentType.appointment_name || "Service Type";
                const duration = appointmentType.appointment_duration
                  ? `(${appointmentType.appointment_duration} mins)`
                  : "";

                let formattedDate = "";
                let formattedTime = "";
                try {
                  const dateObj = new Date(requestedTimestamp);
                  formattedDate = format(dateObj, "EEE, MMM d");
                  formattedTime = format(dateObj, "p"); // e.g. 10:00 AM
                } catch (e) {
                  console.warn("Invalid date/time for appointment ID:", id);
                }

                return (
                  <div className="appointment-card-client" key={id}>
                    <div className="appointment-header">
                      <span className="service-type">{serviceType}</span>
                      {/* Payment Status for Pending Requests */}
                      {activeTab === "pending" && (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {appt.status === "authorization_hold" && (
                            <span style={{
                              backgroundColor: "#fff3cd",
                              color: "#856404",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: "500"
                            }}>
                              Payment Hold
                            </span>
                          )}
                          {appt.status === "pending" && (
                            <span style={{
                              backgroundColor: "#f8d7da",
                              color: "#721c24",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: "500"
                            }}>
                              No Payment
                            </span>
                          )}
                          {appt.status === "payment_captured" && (
                            <span style={{
                              backgroundColor: "#d4edda",
                              color: "#155724",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: "500"
                            }}>
                              Payment Captured
                            </span>
                          )}
                        </div>
                      )}
                      <CardMenu
                        onViewDocuments={() =>
                          handleCardMenuAction("viewDocs", appt)
                        }
                        onReschedule={() =>
                          handleCardMenuAction("reschedule", appt)
                        }
                        onCancel={() => handleCardMenuAction("cancel", appt)}
                      />
                    </div>

                    <div className="lawyer-section">
                      <img
                        src={lawyerPhoto}
                        alt="Lawyer Avatar"
                        className="lawyer-avatar"
                      />
                      <div className="lawyer-info">
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                          }}
                        >
                          <div className="lawyer-name">{lawyerName}</div>
                          {/* 
                                The message button now opens ChatModal with the lawyer object.
                              */}
                          <img
                            src={MessageButton}
                            alt="Message Lawyer"
                            style={{
                              width: "30px",
                              height: "30px",
                              marginLeft: "20px",
                              cursor: "pointer",
                            }}
                            onClick={() => handleMessageButtonClick(lawyer)}
                          />
                        </div>
                        <div className="practice-area">{practiceAreas}</div>
                      </div>
                    </div>

                    <div className="appointment-details">
                      <div>{formattedDate}</div>
                      <div>
                        {formattedTime} {duration}
                      </div>
                    </div>

                    <hr />

                    <div className="appointment-actions">
                      {(activeTab === "upcoming" ) && (
                        <>
                          <button
                            className="join-call-btn"
                            onClick={() => handleJoinMeeting(userInfo, appt)}
                          >
                            Join Meeting
                          </button>
                        </>
                      )}

                      {status === "cancelled" && (
                        <span className="cancelled-badge">Cancelled</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
      {/* ChatModal integration */}
      <ChatModal
        open={chatModalOpen}
        onClose={() => setChatModalOpen(false)}
        lawyerUser={chatLawyer}
      />
      <Dialog 
        open={joinModalOpen} 
        onClose={() => setJoinModalOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            fontFamily: 'Poppins, sans-serif',
          }
        }}
      >
        <DialogTitle 
          style={{ 
            backgroundColor: '#144A8C', 
            color: 'white', 
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600,
            fontSize: '20px',
            textAlign: 'center',
            padding: '24px 24px 16px 24px'
          }}
        >
          ⏰ Meeting Not Yet Available
        </DialogTitle>
        <DialogContent 
          style={{ 
            padding: '32px 24px 24px 24px',
            fontFamily: 'Poppins, sans-serif'
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ 
              fontSize: '16px', 
              color: '#666', 
              marginBottom: '16px',
              fontWeight: 500
            }}>
              This meeting is scheduled for:
            </div>
            <div style={{ 
              color: '#144A8C', 
              fontWeight: 600, 
              fontSize: '18px', 
              marginBottom: '24px',
              lineHeight: '1.4',
              backgroundColor: '#f8f9ff',
              padding: '16px',
              borderRadius: '12px',
              border: '1px solid #e3e8ff'
            }}>
              {joinWindowText}
            </div>
            {joinCountdown > 0 ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                gap: '12px',
                backgroundColor: '#fff3e0',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid #ffe0b2'
              }}>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#f57c00', 
                  fontWeight: 500
                }}>
                  You can join in:
                </div>
                <div style={{ 
                  color: '#f57c00', 
                  fontWeight: 700, 
                  fontSize: '28px',
                  fontFamily: 'monospace',
                  letterSpacing: '2px'
                }}>
                  {formatCountdown(joinCountdown)}
                </div>
              </div>
            ) : (
              <div style={{ 
                backgroundColor: '#ffebee',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid #ffcdd2'
              }}>
                <div style={{ 
                  color: '#d32f2f', 
                  fontWeight: 500,
                  textAlign: 'center'
                }}>
                  The meeting is not currently accessible.
                </div>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions 
          style={{ 
            padding: '0 24px 24px 24px',
            justifyContent: 'center'
          }}
        >
          <Button 
            onClick={() => setJoinModalOpen(false)}
            variant="contained"
            style={{
              backgroundColor: '#144A8C',
              color: 'white',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 500,
              borderRadius: '8px',
              padding: '10px 32px',
              textTransform: 'none',
              fontSize: '16px',
              '&:hover': {
                backgroundColor: '#0d3a6b'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default MyAppointments;
